import { useEffect, forwardRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { addUnsavedWork, markWorkSaved } from '../../../../actions/unsavedwork'
import { hasText } from '../../../../utils/draftjs'

/**
 * HOC that adds automatic unsaved work handling to any Draft editor
 *
 *  - Will assume that any changes to the content of the editor indicate unsaved
 *    work, and that work is only saved on unmount, with these exceptions:
 *    - durring a loom recording associated with this editor, the work is
 *      considered saved as long as loom is active
 *    - if savedWhenEmpty is true, an editor with no text will always be
 *      considered saved
 *  - unsavedWorkId, safePathForUnsavedNav, and isInModal are passed along to
 *    the unsavedwork actions to identify the work and determine what navigation
 *    (if any) will not clear the unsaved work
 *    - both are optional, if unsavedWorkId is left out, the HOC will do nothing
 *  - When used in combination with other HOCs, should generally be applied last
 *    to ensure any changes initiated in another HOC are caught
 *    - The exception to this is enhanceEditor, which should always be last (and
 *      won't trigger any changes of its own)
 */

const withUnsavedWorkWarning = BaseEditor =>
  forwardRef((props, ref) => {
    const {
      editorState,
      onChange,
      unsavedWorkId,
      safePathForUnsavedNav,
      isInModal,
      emptyIsSaved,
      loomData
    } = props

    const dispatch = useDispatch()
    const [contentHasChanged, setContentHasChanged] = useState(false)
    const [markedUnsavedUnderId, setMarkedUnsavedUnderId] = useState(null)

    const onChangeWrapped = newState => {
      const currentContent = editorState.getCurrentContent()
      const newContent = newState.getCurrentContent()

      onChange(newState)

      if (emptyIsSaved && !hasText(newState)) {
        setContentHasChanged(false)
        if (markedUnsavedUnderId) {
          dispatch(markWorkSaved(markedUnsavedUnderId))
          setMarkedUnsavedUnderId(null)
        }
      } else if (currentContent !== newContent) {
        setContentHasChanged(true)

        if (
          unsavedWorkId &&
          (!markedUnsavedUnderId || markedUnsavedUnderId !== unsavedWorkId) &&
          !loomData?.active
        ) {
          dispatch(
            addUnsavedWork(unsavedWorkId, safePathForUnsavedNav, isInModal)
          )
          setMarkedUnsavedUnderId(unsavedWorkId)
        }
      }
    }

    // loom handling
    useEffect(() => {
      if (loomData && unsavedWorkId) {
        if (loomData.active && markedUnsavedUnderId === unsavedWorkId) {
          // durring a recording, unsaved work is persisted to the store
          dispatch(markWorkSaved(markedUnsavedUnderId))
          setMarkedUnsavedUnderId(null)
        } else {
          // recording is no longer active, work is now unsaved
          if (
            contentHasChanged &&
            (!markedUnsavedUnderId || markedUnsavedUnderId !== unsavedWorkId)
          ) {
            dispatch(
              addUnsavedWork(unsavedWorkId, safePathForUnsavedNav, isInModal)
            )
            setMarkedUnsavedUnderId(unsavedWorkId)
          }
        }
      }
    }, [loomData])

    // work is saved or lost on unmount, either way mark it not unsaved
    useEffect(() => {
      return () => {
        if (markedUnsavedUnderId) {
          dispatch(markWorkSaved(markedUnsavedUnderId))
        }
      }
    }, [markedUnsavedUnderId])

    return (
      <BaseEditor
        {...props}
        ref={ref}
        onChange={onChangeWrapped}
        spellCheck="true"
      />
    )
  })

export default withUnsavedWorkWarning
